import Message from 'services/api/Message';
import Preview from 'services/api/Preview';
import { messagesApiUtil } from 'store/app/entities/MessagesApiSlice';
import replaceTempMessageInListDraft from 'store/app/entities/messages/replaceTempMessageInListDraft';
import upsertMessageIntoListDraft from 'store/app/entities/messages/upsertMessageIntoListDraft';
import currentChannel from 'store/selectors/currentChannel';
import getLinkPreview from 'store/selectors/getLinkPreview';

export const LOAD_MESSAGES = 'LOAD_MESSAGES';
export const LOAD_MESSAGES_PENDING = 'LOAD_MESSAGES_PENDING';
export const LOAD_MESSAGES_FULFILLED = 'LOAD_MESSAGES_FULFILLED';
export const LOAD_MESSAGES_REJECTED = 'LOAD_MESSAGES_REJECTED';

export const READ_MESSAGES = 'READ_MESSAGES';
export const READ_MESSAGES_FULFILLED = 'READ_MESSAGES_FULFILLED';

export const GENERATE_MESSAGE_PREVIEW = 'GENERATE_MESSAGE_PREVIEW';
export const GENERATE_MESSAGE_PREVIEW_PENDING = 'GENERATE_MESSAGE_PREVIEW_PENDING';
export const GENERATE_MESSAGE_PREVIEW_FULFILLED = 'GENERATE_MESSAGE_PREVIEW_FULFILLED';
export const GENERATE_MESSAGE_PREVIEW_REJECTED = 'GENERATE_MESSAGE_PREVIEW_REJECTED';

export const SEND_MESSAGE = 'SEND_MESSAGE';
export const SEND_MESSAGE_PENDING = 'SEND_MESSAGE_PENDING';
export const SEND_MESSAGE_FULFILLED = 'SEND_MESSAGE_FULFILLED';
export const SEND_MESSAGE_REJECTED = 'SEND_MESSAGE_REJECTED';
export const ADD_MESSAGE = 'ADD_MESSAGE';

export const CONFIRM_ACKNOWLEDGE_MESSAGE = 'CONFIRM_ACKNOWLEDGE_MESSAGE';
export const CONFIRM_ACKNOWLEDGE_MESSAGE_PENDING = 'CONFIRM_ACKNOWLEDGE_MESSAGE_PENDING';
export const CONFIRM_ACKNOWLEDGE_MESSAGE_FULFILLED = 'CONFIRM_ACKNOWLEDGE_MESSAGE_FULFILLED';
export const CONFIRM_ACKNOWLEDGE_MESSAGE_REJECTED = 'CONFIRM_ACKNOWLEDGE_MESSAGE_REJECTED';

export const UPDATE_MESSAGE = 'UPDATE_MESSAGE';
export const UPDATE_MESSAGE_FULFILLED = 'UPDATE_MESSAGE_FULFILLED';
export const UPDATE_MESSAGE_REJECTED = 'UPDATE_MESSAGE_REJECTED';

export const DESTROY_MESSAGE = 'DESTROY_MESSAGE';
export const DESTROY_MESSAGE_PENDING = 'DESTROY_MESSAGE_PENDING';
export const DESTROY_MESSAGE_FULFILLED = 'DESTROY_MESSAGE_FULFILLED';
export const DESTROY_MESSAGE_REJECTED = 'DESTROY_MESSAGE_REJECTED';

export const ON_READ_MESSAGE = 'ON_READ_MESSAGE';

export const DIGITAL_SIGNATURE_MESSAGE = 'DIGITAL_SIGNATURE_MESSAGE';
export const DIGITAL_SIGNATURE_MESSAGE_PENDING = 'DIGITAL_SIGNATURE_MESSAGE_PENDING';
export const DIGITAL_SIGNATURE_MESSAGE_FULFILLED = 'DIGITAL_SIGNATURE_MESSAGE_FULFILLED';
export const DIGITAL_SIGNATURE_MESSAGE_REJECTED = 'DIGITAL_SIGNATURE_MESSAGE_REJECTED';

export const SEARCH_MESSAGES = 'SEARCH_MESSAGES';
export const SEARCH_MESSAGES_PENDING = 'SEARCH_MESSAGES_PENDING';
export const SEARCH_MESSAGES_FULFILLED = 'SEARCH_MESSAGES_FULFILLED';
export const SEARCH_MESSAGES_REJECTED = 'SEARCH_MESSAGES_REJECTED';

export const MARK_AS_READ_MESSAGES = 'MARK_AS_READ_MESSAGES';

export const PERMISSIONS_MESSAGE = 'PERMISSIONS_MESSAGE';
export const PERMISSIONS_MESSAGE_PENDING = 'PERMISSIONS_MESSAGE_PENDING';
export const PERMISSIONS_MESSAGE_FULFILLED = 'PERMISSIONS_MESSAGE_FULFILLED';
export const PERMISSIONS_MESSAGE_REJECTED = 'PERMISSIONS_MESSAGE_REJECTED';

export const PIN_MESSAGE = 'PIN_MESSAGE';
export const PIN_MESSAGE_PENDING = 'PIN_MESSAGE_PENDING';
export const PIN_MESSAGE_FULFILLED = 'PIN_MESSAGE_FULFILLED';
export const PIN_MESSAGE_REJECTED = 'PIN_MESSAGE_REJECTED';

export const UNPIN_MESSAGE = 'UNPIN_MESSAGE';
export const UNPIN_MESSAGE_PENDING = 'UNPIN_MESSAGE_PENDING';
export const UNPIN_MESSAGE_FULFILLED = 'UNPIN_MESSAGE_FULFILLED';
export const UNPIN_MESSAGE_REJECTED = 'UNPIN_MESSAGE_REJECTED';

export const ADD_REACTION = 'ADD_REACTION';
export const ADD_REACTION_PENDING = 'ADD_REACTION_PENDING';
export const ADD_REACTION_FULFILLED = 'ADD_REACTION_FULFILLED';
export const ADD_REACTION_REJECTED = 'ADD_REACTION_REJECTED';

export const REMOVE_REACTION = 'REMOVE_REACTION';
export const REMOVE_REACTION_PENDING = 'REMOVE_REACTION_PENDING';
export const REMOVE_REACTION_FULFILLED = 'REMOVE_REACTION_FULFILLED';
export const REMOVE_REACTION_REJECTED = 'REMOVE_REACTION_REJECTED';

const generatePreview = (url, messageId, channelId) => (dispatch, getState) => {
  const cached = getLinkPreview(getState(), url);
  const reuseCache = cached && !cached.imagePending;
  return dispatch({
    type: GENERATE_MESSAGE_PREVIEW,
    payload: reuseCache ? Promise.resolve(cached) : Preview.generate(url),
    meta: { link: url, message_id: messageId, channel_id: channelId },
  });
};

const load =
  (channelId, { replace, ...options } = {}) =>
  (dispatch, getState) =>
    dispatch({
      type: LOAD_MESSAGES,
      payload: Message.list(channelId, options),
      meta: {
        channel_id: channelId,
        allMessages: currentChannel(getState())?.messages || [],
        currentId: getState().elements.ChannelsPanel.currentId,
        complete: ['beforeId', 'aroundId'].every(key => !options[key]),
        replace,
        ...options,
      },
    });

/** redux-promise-middleware may resolve the dispatch as `{ value }`, an FSA, or `{ action }`. */
export function listLoadPayload(result) {
  return result?.value ?? result?.payload ?? result?.action?.payload ?? null;
}

export function messageInListPayload(payload, messageId) {
  if (messageId == null || !Array.isArray(payload?.messages)) {
    return null;
  }
  return payload.messages.find(item => String(item._id) === String(messageId)) ?? null;
}

/**
 * Reload one message and write it into the chat list cache. `load` only updates the
 * legacy messages store; MessagesList reads `listMessages`.
 */
const loadListedMessage = (channelId, messageId) => async dispatch => {
  const result = await dispatch(load(channelId, { aroundId: messageId }));
  const fresh = messageInListPayload(listLoadPayload(result), messageId);
  if (!fresh) {
    return null;
  }
  dispatch(
    messagesApiUtil.updateQueryData('listMessages', { channelId }, draft => {
      upsertMessageIntoListDraft(draft, fresh);
    }),
  );
  return fresh;
};

const search = s => ({
  type: SEARCH_MESSAGES,
  payload: Message.search(s),
});

const read =
  (channelId, updateFirstRead = false) =>
  (dispatch, getState) => {
    if (getState().auth?.isSuperAdmin) {
      return undefined;
    }
    const channel = currentChannel(getState());
    const messages = channel?.messages || [];
    const oldestLoadedMessageId = messages[0]?._id;
    const wouldUpdateFirst =
      updateFirstRead &&
      (!channel?.firstReadMessage ||
        (oldestLoadedMessageId && oldestLoadedMessageId < channel.firstReadMessage));
    const firstReadMessage = wouldUpdateFirst ? oldestLoadedMessageId : undefined;
    return dispatch({
      type: READ_MESSAGES,
      payload: Message.read(channelId, [], firstReadMessage),
      meta: { channel_id: channelId, authUser_id: getState().auth.user_id },
    });
  };

const send = message => (dispatch, getState) => {
  const { elements, auth } = getState();
  const { watchedChannel_id: watchedChannelId } = elements.ChatPanel;
  const channelId = message.channel_id;
  const tempId = message._id;

  // Optimistic: add temp message to RTK Query cache (arg must match useListMessagesQuery({ channelId }))
  if (channelId) {
    dispatch(
      messagesApiUtil.updateQueryData('listMessages', { channelId }, draft => {
        if (draft && !draft.messages.find(m => m._id === tempId)) {
          draft.messages.unshift({ ...message, temp: true });
        }
      }),
    );
  }

  const promise = dispatch({
    type: SEND_MESSAGE,
    payload: Message.send(message),
    meta: {
      message_id: tempId,
      message,
      watchedChannel_id: watchedChannelId,
      authUserId: auth.user_id,
    },
  });

  // Replace temp with server-confirmed message in RTK Query cache.
  // `newMessage` socket often unshifts the same server `_id` before this runs — strip those first
  // so we never keep [server, temp→server] duplicates.
  // redux-promise-middleware resolves inner dispatch to `{ value, action }`, not an FSA — use `.value`.
  promise?.then?.(result => {
    const real = result?.value ?? result?.action?.payload;
    if (real?._id && channelId) {
      dispatch(
        messagesApiUtil.updateQueryData('listMessages', { channelId }, draft => {
          replaceTempMessageInListDraft(draft, { tempId, real });
        }),
      );
    }
  });
};

const add = message => (dispatch, getState) =>
  dispatch({
    type: ADD_MESSAGE,
    payload: message,
    meta: {
      currentId: getState().elements.ChannelsPanel.currentId,
      authUserId: getState().auth.user_id,
      watchedChannel_id: getState().elements.ChatPanel.watchedChannel_id,
      // Relevant to set the number of unread messages:
      // If the page is hidden (tab is not active) the message should be considered
      // unread even if the channel is the current on
      hasFocus: getState().elements.Focus.hasFocus,
    },
  });

const addAndMaybeRead = message => async (dispatch, getState) => {
  await dispatch(add(message));

  // Write to RTK Query cache so MessagesList picks it up. Upsert: the backend
  // re-emits the same _id with late data (async thumb metadata, processingFailed).
  const channelId = message.channel?._id || message.channel;
  if (channelId) {
    dispatch(
      messagesApiUtil.updateQueryData('listMessages', { channelId }, draft => {
        upsertMessageIntoListDraft(draft, message);
      }),
    );
  }

  const currentChannelId = currentChannel(getState())?._id;

  // Mark as read if we add a new message in the current open channel and the documnent has focus
  if (message.channel === currentChannelId && getState().elements.Focus.hasFocus) {
    dispatch(read(currentChannelId));
  }
};

/**
 * Voice agent word-by-word streaming: patch the text of an already-rendered message
 * in the RTK Query `listMessages` cache. The message is a real message (real `_id`)
 * created up front by the backend, so no temp/dedup logic is needed here.
 */
const streamText =
  ({ _id, text, channelId, message }) =>
  dispatch => {
    if (!channelId || !_id) return;
    dispatch(
      messagesApiUtil.updateQueryData('listMessages', { channelId }, draft => {
        if (!draft?.messages) return;
        const existing = draft.messages.find(m => m._id === _id);
        if (existing) {
          existing.text = text;
        } else if (message) {
          // First stream event may arrive before `newMessage` — insert the built message.
          draft.messages.unshift({ ...message, text });
        }
      }),
    );
  };

const confirmAcknowledge = messageId => (dispatch, getState) =>
  dispatch({
    type: CONFIRM_ACKNOWLEDGE_MESSAGE,
    payload: Message.confirm(messageId),
    meta: {
      channel_id: getState().elements.ChannelsPanel.currentId,
      message_id: messageId,
      authUser_id: getState().auth.user_id,
    },
  });

const ensureAcknowledgmentReceipt = (messageId, userId) => async (dispatch, getState) => {
  const payload = await Message.ensureAcknowledgmentReceipt(messageId, userId);
  const message = payload?.message;
  if (!message?._id) {
    return null;
  }
  await dispatch(add(message));
  const channelId = message.channel_id || message.channel || currentChannel(getState())?._id;
  if (channelId) {
    dispatch(
      messagesApiUtil.updateQueryData('listMessages', { channelId }, draft => {
        if (!draft?.messages) return;
        const idx = draft.messages.findIndex(m => String(m._id) === String(message._id));
        if (idx >= 0) {
          const row = draft.messages[idx];

          draft.messages[idx] = { ...row, acknowledges: message.acknowledges };
        }
      }),
    );
  }
  return message;
};

const update = message => (dispatch, getState) => {
  const channelId = message.channel_id || currentChannel(getState())?._id;
  const promise = dispatch({
    type: UPDATE_MESSAGE,
    payload: Message.update(message),
    meta: { message_id: message._id, message },
  });
  promise?.then?.(result => {
    const updated = result?.value ?? result?.action?.payload ?? message;
    if (channelId) {
      dispatch(
        messagesApiUtil.updateQueryData('listMessages', { channelId }, draft => {
          if (!draft) return;
          const idx = draft.messages.findIndex(m => m._id === message._id);
          if (idx >= 0) draft.messages[idx] = { ...draft.messages[idx], ...updated };
        }),
      );
    }
  });
};

const updateSuccess = message => ({
  type: UPDATE_MESSAGE_FULFILLED,
  payload: message,
  meta: { message_id: message._id, message },
});

const markAsRead = channelId => (dispatch, getState) => {
  if (getState().auth?.isSuperAdmin) {
    return undefined;
  }
  return dispatch({
    type: MARK_AS_READ_MESSAGES,
    payload: channelId,
    meta: { channel_id: channelId, authUser_id: getState().auth.user_id },
  });
};

const loadAndRead =
  (channelId, ...params) =>
  async dispatch => {
    await dispatch(load(channelId, ...params));

    dispatch(read(channelId));
  };

/** Chat UI reads `useListMessagesQuery` (RTK cache), not legacy `messages` entities — keep cache in sync on delete. */
function patchMessageListCacheAfterDelete(dispatch, channelId, messageId, serverMessage) {
  if (!channelId || !serverMessage) return;
  dispatch(
    messagesApiUtil.updateQueryData('listMessages', { channelId }, draft => {
      if (!draft?.messages) return;
      const idx = draft.messages.findIndex(m => String(m._id) === String(messageId));
      if (idx < 0) {
        return;
      }
      if (serverMessage.deletedAt) {
        draft.messages.splice(idx, 1);
      } else if (serverMessage.softDeletedAt) {
        const row = draft.messages[idx];
        row.text = serverMessage.text ?? row.text;
        row.softDeletedAt = serverMessage.softDeletedAt;
        row.softDeletedBy = serverMessage.softDeletedBy;
        row.isEdited = serverMessage.isEdited ?? row.isEdited;
        row.canDelete = serverMessage.canDelete ?? row.canDelete;
        row.canEdit = serverMessage.canEdit ?? row.canEdit;
        if (serverMessage.attachments) {
          row.attachments = serverMessage.attachments;
        }
        if (serverMessage.blocks) {
          row.blocks = serverMessage.blocks;
        }
      }
    }),
  );
}

const destroy = messageId => (dispatch, getState) => {
  const channel = currentChannel(getState());
  const { _id } = channel;
  const lastMessageId = channel.lastMessage_id;

  const promise = dispatch({
    type: DESTROY_MESSAGE,
    payload: Message.delete(messageId),
    meta: {
      message_id: messageId,
      lastMessage_id: lastMessageId,
      channel_id: _id,
    },
  });

  promise?.then?.(result => {
    const body = result?.value ?? result?.action?.payload;
    const deletedMessage = body?.data?.message ?? body?.message;
    if (_id && deletedMessage) {
      patchMessageListCacheAfterDelete(dispatch, _id, messageId, deletedMessage);
    }
  });
  return promise;
};

const destroyFulfilled = message => (dispatch, getState) => {
  const channel = currentChannel(getState());
  const { _id } = channel;
  const lastMessageId = channel.lastMessage_id;

  const ret = dispatch({
    type: DESTROY_MESSAGE_FULFILLED,
    payload: {
      message,
    },
    meta: {
      message_id: message._id,
      lastMessage_id: lastMessageId,
      channel_id: _id,
    },
  });
  if (_id && message) {
    patchMessageListCacheAfterDelete(dispatch, _id, message._id, message);
  }
  return ret;
};

const onReadMessage = payload => ({
  type: ON_READ_MESSAGE,
  payload,
});

const addDigitalSignature = messageId => ({
  type: DIGITAL_SIGNATURE_MESSAGE,
  payload: Message.digitalSignature(messageId),
});

const permissions = messageId => ({
  type: PERMISSIONS_MESSAGE,
  payload: Message.permissions(messageId),
  meta: {
    message_id: messageId,
  },
});

const pin = message => (dispatch, getState) => {
  const { _id: channelId, pinnedMessage } = currentChannel(getState());
  return dispatch({
    type: PIN_MESSAGE,
    payload: Message.pin(message._id),
    meta: { message, channelId, pinnedMessage },
  });
};

const unpin = message => (dispatch, getState) => {
  const { _id: channelId, pinnedMessage } = currentChannel(getState());
  return dispatch({
    type: UNPIN_MESSAGE,
    payload: Message.unpin(message._id),
    meta: { message, channelId, pinnedMessage },
  });
};

const addReaction = (messageId, reaction) => ({
  type: ADD_REACTION,
  payload: Message.addReaction(messageId, reaction),
  meta: { messageId, reaction },
});

const removeReaction = (messageId, reaction) => ({
  type: REMOVE_REACTION,
  payload: Message.removeReaction(messageId, reaction),
  meta: { messageId, reaction },
});

export default {
  add,
  addAndMaybeRead,
  addDigitalSignature,
  confirmAcknowledge,
  ensureAcknowledgmentReceipt,
  destroy,
  destroyFulfilled,
  generatePreview,
  load,
  loadListedMessage,
  loadAndRead,
  markAsRead,
  onReadMessage,
  permissions,
  pin,
  read,
  search,
  send,
  streamText,
  unpin,
  update,
  updateSuccess,
  addReaction,
  removeReaction,
};
